import { CheckboxGroupFormComponent } from "components/Form/components/CheckboxGroup/CheckboxGroupFormComponent";
import { Form } from "components/Form/components/Form/Form";
import { TextFormComponent } from "components/Form/components/Text/TextFormComponent";
import { Popover, popoverPosition } from "components/Popover/Popover";
import "containers/AdvancedSearch/AdvancedSearch.scss";
import { CheckboxTriggerButton } from "containers/AdvancedSearch/components/CheckboxTriggerButton";
import { RecordTypeFilter } from "containers/AdvancedSearch/components/RecordTypeFilter";
import { getFilterStorageKey } from "containers/ExecutionFilter/filterStorage";
import { RecordLabelProps } from "containers/AdvancedSearch/utils";
import "containers/ExecutionFilter/ExecutionFilter.scss";
import {
    getValuesFromQuery,
    onCheckboxValueChange,
    submitFilterForm,
    useSetQueryString,
} from "containers/ExecutionFilter/utils";
import { ClearSearchButton } from "containers/Filter/components/ClearSearchButton";
import { SearchButton } from "containers/Filter/components/SearchButton";
import clearSearchStyles from "containers/Filter/components/SearchButton.module.scss";
import { LogFilterValues } from "containers/Resources/screens/ResourcesSingle/components/RecordLogList/ContainerLogList";
import { History } from "history";
import { useCallback, useEffect, useMemo } from "react";
import { Field, Form as FinalForm } from "react-final-form";
import { useMount } from "react-use";
import { capitalizeFirstLetterRestLowercase } from "utils/utils";
import { getLogLevelOptionClassName, getRecordLogLevelList } from "./constants";
import "./LogListFilter.scss";

const useLogFilters = ({
    queryParams,
    history,
    isWorkspace,
    filterContext,
}: {
    queryParams: { [key: string]: string };
    history: History;
    isWorkspace?: boolean;
    filterContext?: { recordType: string; recordId: string };
}) => {
    const filterStorageKey = filterContext
        ? getFilterStorageKey({
              page: "log-filter",
              recordType: filterContext.recordType,
              recordId: filterContext.recordId,
          })
        : undefined;

    const { setQueryString, setInitialQueryString, effectiveQueryParams, hasStoredFilter } =
        useSetQueryString(queryParams, history, filterStorageKey);

    const { findValueInFilterQuery, findEncodedValueInFilterQuery } = useMemo(
        () => getValuesFromQuery(effectiveQueryParams),
        [effectiveQueryParams]
    );

    const parsedFilteredValues: LogFilterValues = useMemo(() => {
        const values: LogFilterValues = {
            subjectOrData: findEncodedValueInFilterQuery("subjectOrData"),
            level: findValueInFilterQuery("level").array,
        };
        if (isWorkspace) {
            values.recordLogName = findEncodedValueInFilterQuery("recordLogName");
            values.recordLogType = findValueInFilterQuery("recordLogType").array;
        }
        return values;
    }, [findEncodedValueInFilterQuery, findValueInFilterQuery, isWorkspace]);

    return {
        filteredValues: parsedFilteredValues,
        setQueryString,
        setInitialQueryString,
        effectiveQueryParams,
        hasStoredFilter,
    };
};

interface LogListFilterProps {
    initialFilter: { [key: string]: any };
    clearFilter?: { [key: string]: any };
    recordList?: RecordLabelProps[];
    isWorkspace?: boolean;
    filterContext?: { recordType: string; recordId: string };
    additionalParams?: { [key: string]: string };
    setFilterOnMount?: boolean;
    history: History;
    queryParams: { [key: string]: string };
    isDisabled?: boolean;
    onEffectiveQueryParamsChange?: (queryParams: { [key: string]: string }) => void;
}

export const LogListFilter = ({
    initialFilter,
    clearFilter,
    recordList,
    isWorkspace,
    filterContext,
    additionalParams,
    setFilterOnMount,
    history,
    queryParams,
    isDisabled,
    onEffectiveQueryParamsChange,
}: LogListFilterProps) => {
    const { filteredValues, setQueryString, setInitialQueryString, effectiveQueryParams, hasStoredFilter } =
        useLogFilters({ queryParams, history, isWorkspace, filterContext });
    const { recordLogType, level } = filteredValues;

    useEffect(() => {
        onEffectiveQueryParamsChange?.(effectiveQueryParams);
    }, [effectiveQueryParams, onEffectiveQueryParamsChange]);

    const setInitialFilterHandler = useCallback(
        (historyMethod?: "push" | "replace") =>
            setInitialQueryString(initialFilter, historyMethod, additionalParams),
        [additionalParams, initialFilter, setInitialQueryString]
    );

    const setClearFilterHandler = useCallback(
        (historyMethod?: "push" | "replace") =>
            setInitialQueryString(clearFilter ?? initialFilter, historyMethod, additionalParams),
        [additionalParams, clearFilter, initialFilter, setInitialQueryString]
    );

    useMount(() => {
        if (!hasStoredFilter || setFilterOnMount) {
            setInitialFilterHandler("replace");
        }
    });

    return (
        <FinalForm
            keepDirtyOnReinitialize
            onSubmit={(formData) =>
                submitFilterForm({
                    prevValues: filteredValues,
                    newValues: formData,
                    setQueryString,
                    textFields: ["recordLogName", "subjectOrData"],
                })
            }
            initialValues={filteredValues}
        >
            {({ handleSubmit, values, form, pristine }) => {
                // Pristine-guarded submit for the level/type facet popovers (applied
                // on popover close): no-op when nothing changed.
                const submitForm = () => {
                    if (!pristine) {
                        handleSubmit();
                    }
                };
                // Explicit search trigger (Search button + Enter). Always applies the
                // current filter, even if the text field is unchanged.
                const runSearch = () => {
                    handleSubmit();
                };
                // IMPORTANT: this filter deliberately renders NO <form> element. It is
                // mounted INSIDE the record screen's main <form id="resourcesSingleForm">,
                // and nested <form> elements are invalid HTML — the browser does not
                // reliably honour React's delegated onSubmit/preventDefault on the
                // inner form, so a native (GET) submit navigated to the pathname with
                // just the search field as the query string, DROPPING the record `id`
                // and `logs` sub-page flags and landing on the route catch-all
                // ("Sorry, we cannot find what you're looking for. Please check the
                // link."). Rendering a plain container + submitting explicitly makes a
                // native navigation impossible in any browser, nested or not.
                // (operator-task-fe-record-log-search-field-triggers-not-found-screen)
                const onKeyDown = (event: any) => {
                    if (event.key === "Enter") {
                        // Also stop Enter from implicitly submitting the enclosing
                        // record form (which would attempt to save the record).
                        event.preventDefault();
                        runSearch();
                    }
                };
                return (
                    <div className="form" onKeyDown={onKeyDown}>
                        <div
                            className={`execution-search-filters advanced-search-filters ${
                                isDisabled ? "disabledSearch" : ""
                            }`}
                        >
                            {isWorkspace && (
                                <Form.Column>
                                    <Field
                                        name="recordLogName"
                                        id="recordLogName"
                                        fieldAttr={{
                                            placeholder: "Search by name or id",
                                        }}
                                        component={TextFormComponent}
                                    />
                                </Form.Column>
                            )}
                            <Form.Column>
                                <Field
                                    name="subjectOrData"
                                    id="subjectOrData"
                                    fieldAttr={{
                                        placeholder: "Search by subject or data",
                                    }}
                                    component={TextFormComponent}
                                />
                            </Form.Column>
                            {isWorkspace &&
                                recordList !== undefined &&
                                recordLogType !== undefined && (
                                    <Form.Column>
                                        <Popover
                                            trigger={
                                                <CheckboxTriggerButton
                                                    label="Type"
                                                    currentFilter={recordLogType}
                                                    totalItemCount={recordList.length}
                                                />
                                            }
                                            contentClassName="checkbox-group-filter"
                                            closeOnInsideClick={false}
                                            onBlur={submitForm}
                                            {...popoverPosition}
                                        >
                                            <RecordTypeFilter
                                                name="recordLogType"
                                                totalResourcesNumber={recordList.length}
                                                resourceArray={recordList.map(
                                                    (record) => record.resourceName
                                                )}
                                                resourceList={recordList.map((record) => ({
                                                    value: record.resourceName,
                                                    label: record.label,
                                                }))}
                                                onChange={(identifier, newValue) => (e) => {
                                                    onCheckboxValueChange({
                                                        form,
                                                        identifier,
                                                        currentValue: values[identifier],
                                                        newValue,
                                                    })(e);
                                                }}
                                                onAllValuesChange={(identifier, newValues) =>
                                                    form.change(identifier, newValues)
                                                }
                                                resourceTypeFilter={values.recordLogType}
                                            />
                                        </Popover>
                                        {/* <Popover
                                            trigger={
                                                <CheckboxTriggerButton
                                                    label="Type"
                                                    currentFilter={recordLogType}
                                                    totalItemCount={recordList.length}
                                                />
                                            }
                                            closeOnInsideClick={false}
                                            onBlur={submitForm}
                                            {...popoverPosition}
                                        >
                                            <Field
                                                name="recordLogType"
                                                id="recordLogType"
                                                component={CheckboxGroupFormComponent}
                                                fieldProps={{
                                                    options: recordList.map((record) => ({
                                                        value: record.resourceName,
                                                        label: record.label,
                                                    })),
                                                    currentValue: values.recordLogType,
                                                    onChange: (newValue) => (e) => {
                                                        onCheckboxValueChange({
                                                            form,
                                                            identifier: "recordLogType",
                                                            currentValue: values.recordLogType,
                                                            newValue,
                                                        })(e);
                                                    },
                                                }}
                                            />
                                        </Popover> */}
                                    </Form.Column>
                                )}

                            <Form.Column>
                                <Popover
                                    trigger={
                                        <CheckboxTriggerButton
                                            label="Level"
                                            currentFilter={level}
                                            totalItemCount={getRecordLogLevelList().length}
                                        />
                                    }
                                    contentClassName="log-level-filter"
                                    closeOnInsideClick={false}
                                    onBlur={submitForm}
                                    {...popoverPosition}
                                >
                                    <Field
                                        name="level"
                                        id="level"
                                        component={CheckboxGroupFormComponent}
                                        fieldProps={{
                                            options: getRecordLogLevelList().map((level) => ({
                                                value: level,
                                                label: capitalizeFirstLetterRestLowercase(level),
                                            })),
                                            getOptionClassName: getLogLevelOptionClassName,
                                            currentValue: values.level,
                                            onChange: (newValue) => (e) => {
                                                onCheckboxValueChange({
                                                    form,
                                                    identifier: "level",
                                                    currentValue: values.level,
                                                    newValue,
                                                })(e);
                                            },
                                        }}
                                    />
                                </Popover>
                            </Form.Column>

                            <SearchButton
                                onSearch={runSearch}
                                className={clearSearchStyles.clearSearchButtonSingle}
                            />
                            <ClearSearchButton
                                onClear={() => setClearFilterHandler("push")}
                                className={clearSearchStyles.clearSearchButtonSingle}
                            />
                        </div>
                    </div>
                );
            }}
        </FinalForm>
    );
};
